import { useCallback, useEffect, useMemo, useState } from 'react'
import { Modal, Tooltip } from 'antd'
import { Button, Spin } from '@crosscode/gravity-components'
import { fetchCalendarPeriod } from './api'
import {
  MONTH_NAMES,
  WEEKDAYS,
  buildMonthGrid,
  cellNoteText,
  parseDayMap,
} from './utils'
import styles from './CalendarModal.module.css'

interface Props {
  open: boolean
  onClose: () => void
}

async function getServerNow(): Promise<Date> {
  try {
    const res = await fetch('/api/time', { credentials: 'include' })
    if (!res.ok) throw new Error('time')
    const data = (await res.json()) as { ms?: number }
    return new Date(data.ms || Date.now())
  } catch {
    return new Date()
  }
}

export function CalendarModal({ open, onClose }: Props) {
  const [year, setYear] = useState(() => new Date().getFullYear())
  const [month, setMonth] = useState(() => new Date().getMonth() + 1)
  const [today, setToday] = useState(() => new Date())
  const [dayMap, setDayMap] = useState<Map<number, { type_id: number; note: string }>>(
    () => new Map(),
  )
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (y: number, m: number) => {
    setLoading(true)
    setError(null)
    try {
      const [data, now] = await Promise.all([fetchCalendarPeriod(y, m), getServerNow()])
      setToday(now)
      if (!data.days?.length) {
        setDayMap(new Map())
        setError('Нет данных за период')
        return
      }
      setDayMap(parseDayMap(data.days))
    } catch (err) {
      setDayMap(new Map())
      setError(err instanceof Error ? err.message : 'Не удалось загрузить календарь')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!open) return
    void (async () => {
      const now = await getServerNow()
      setToday(now)
      setYear(now.getFullYear())
      setMonth(now.getMonth() + 1)
      await load(now.getFullYear(), now.getMonth() + 1)
    })()
  }, [open, load])

  const goPrev = () => {
    let m = month - 1
    let y = year
    if (m < 1) {
      m = 12
      y -= 1
    }
    setMonth(m)
    setYear(y)
    void load(y, m)
  }

  const goNext = () => {
    let m = month + 1
    let y = year
    if (m > 12) {
      m = 1
      y += 1
    }
    setMonth(m)
    setYear(y)
    void load(y, m)
  }

  const cells = useMemo(
    () => buildMonthGrid(year, month, dayMap, today),
    [year, month, dayMap, today],
  )

  return (
    <Modal
      title="Производственный календарь (РФ)"
      open={open}
      onCancel={onClose}
      footer={
        <Button onClick={onClose}>Закрыть</Button>
      }
      width={640}
      destroyOnHidden
    >
      <div className={styles.nav}>
        <button type="button" className={styles.navBtn} aria-label="Предыдущий месяц" onClick={goPrev}>
          ‹
        </button>
        <div className={styles.monthYear}>
          {MONTH_NAMES[month - 1]} {year}
        </div>
        <button type="button" className={styles.navBtn} aria-label="Следующий месяц" onClick={goNext}>
          ›
        </button>
      </div>

      {loading ? (
        <div className={styles.state}>
          <Spin description="Загрузка…" />
        </div>
      ) : error && dayMap.size === 0 ? (
        <div className={`${styles.state} ${styles.error}`}>{error}</div>
      ) : (
        <>
          <div className={styles.grid}>
            {WEEKDAYS.map((wd) => (
              <span key={wd} className={styles.weekday}>
                {wd}
              </span>
            ))}
            {cells.map((cell, index) => {
              if (cell.day == null) {
                return <span key={`e-${index}`} className={`${styles.day} ${styles.dayEmpty}`} />
              }
              const note = cellNoteText(cell.typeId, cell.note)
              const tip = cell.note ? `${cell.label}\n${cell.note}` : cell.label
              const kindClass =
                cell.kind === 'weekend'
                  ? styles.dayWeekend
                  : cell.kind === 'holiday'
                    ? styles.dayHoliday
                    : cell.kind === 'short'
                      ? styles.dayShort
                      : cell.kind === 'transfer'
                        ? styles.dayTransfer
                        : styles.dayWork
              return (
                <Tooltip key={`${year}-${month}-${cell.day}`} title={<span style={{ whiteSpace: 'pre-line' }}>{tip}</span>}>
                  <span
                    className={`${styles.day} ${kindClass}${cell.isToday ? ` ${styles.dayToday}` : ''}`}
                  >
                    <span>{cell.day}</span>
                    {note ? <span className={styles.note}>{note}</span> : null}
                  </span>
                </Tooltip>
              )
            })}
          </div>

          <div className={styles.legend}>
            <span className={styles.legendItem}>
              <i className={`${styles.swatch} ${styles.swatchWork}`} /> Рабочий
            </span>
            <span className={styles.legendItem}>
              <i className={`${styles.swatch} ${styles.swatchWeekend}`} /> Выходной
            </span>
            <span className={styles.legendItem}>
              <i className={`${styles.swatch} ${styles.swatchHoliday}`} /> Праздник
            </span>
            <span className={styles.legendItem}>
              <i className={`${styles.swatch} ${styles.swatchShort}`} /> Сокращённый
            </span>
            <span className={styles.legendItem}>
              <i className={`${styles.swatch} ${styles.swatchTransfer}`} /> Перенос
            </span>
          </div>
        </>
      )}
    </Modal>
  )
}
